import React, { FC } from 'react';
import { InAppNotification } from 'models/InAppNotification/inAppNotification.model';
import styles from './inAppNotificationCard.module.scss';
import { ReactComponent as ScheduleIcon } from 'assets/icons/schedule_fill.svg';
import clsx from 'clsx';
import moment from 'moment';
import { generatePath, useNavigate } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import { CommodityTabsKeys } from 'enums/FarmPageEnum';
import { NotificationRedirectionEvent } from 'enums/NotificationEnums';
import { notificationVariableKeys } from 'constants/constants';
import FarmboxService from 'services/Farmbox/farmbox.service';
import {
  stageMapping,
  statusMappingWithStage,
} from 'views/FarmProductPage/constant';

interface InAppNotificationCardProps {
  notification: InAppNotification;
  markAsRead: (id: string) => void;
}

const {
  PRODUCT_APPROVE,
  PRODUCT_DECLINE,
  ACCOUNT_ACTIVATION,
  PAYMENT_RENEWAL,
  FARM_COMMODITY_DECLINED,
  FARM_COMMODITY_APPROVED,
} = NotificationRedirectionEvent;

const { productId, commodityId } = notificationVariableKeys;

const InAppNotificationCard: FC<InAppNotificationCardProps> = props => {
  const { notification, markAsRead } = props;
  const navigate = useNavigate();
  const {
    redirectToCommodityOverview,
    redirectToFarm,
    redirectToRespectiveCommodityStage,
  } = useFarmboxRedirections();
  const { getCommodity } = FarmboxService();

  const getCurrentTime = () => moment(notification.createdAt).fromNow();

  const redirectToCommodity = async (commodityId: number) => {
    const commodityData = await getCommodity(Number(commodityId));
    const navigationParams = {
      isFrom: CommodityTabsKeys.ALL_COMMODITIES,
      page: 1,
    };
    if (!commodityData) {
      redirectToFarm(CommodityTabsKeys.ALL_COMMODITIES, navigationParams);
      return;
    }

    if (commodityData.status === statusMappingWithStage.draft) {
      const nextStage = commodityData.stage
        ? Number(commodityData.stage + 1)
        : 1;
      const mappedStage = stageMapping[nextStage];

      redirectToRespectiveCommodityStage(
        Number(commodityId),
        mappedStage,
        statusMappingWithStage.draft,
        navigationParams
      );
      return;
    }
    redirectToCommodityOverview(
      commodityId,
      commodityData.status,
      navigationParams
    );
  };

  const redirectToRespectivePage = async () => {
    markAsRead(notification.id ?? '');
    const notificationVariables = JSON.parse(notification?.variables ?? '{}');
    const { event } = notification;
    let path = '';
    switch (event) {
      case PRODUCT_APPROVE:
        path = generatePath(AppRoutes.PRODUCT_DASHBOARD, {
          id: notificationVariables[productId],
        });
        break;

      case PRODUCT_DECLINE:
        path = generatePath(AppRoutes.PRODUCT_OVERVIEW, {
          id: notificationVariables[productId],
        });
        break;

      case ACCOUNT_ACTIVATION:
      case PAYMENT_RENEWAL:
        path = generatePath(AppRoutes.DASHBOARD);
        break;
      /** 
          Have included a return for the below cases since 
          there's an existing path navigation for the previous items
        */
      case FARM_COMMODITY_DECLINED:
      case FARM_COMMODITY_APPROVED:
        await redirectToCommodity(notificationVariables[commodityId]);
        return;
      default:
        return;
    }
    navigate(path, { state: { fromNotification: true } });
  };

  return (
    <div
      className={styles.inAppNotificationCard}
      onClick={redirectToRespectivePage}>
      <div
        className={clsx({
          [styles.isRead]: notification.isRead,
          [styles.notificationItem]: true,
        })}>
        <div
          className={clsx({
            [styles.notificationTitle]: true,
          })}>
          {notification.title}
        </div>
        <div className={styles.notificationMessage}>{notification.message}</div>
        <p className={styles.notificationTime}>
          <ScheduleIcon className={styles.icon} />
          <span>{getCurrentTime()}</span>
        </p>
      </div>
    </div>
  );
};

export default InAppNotificationCard;
